import React, { useState } from "react";
import { Flag, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, apiError, REPORT_REASON_LABELS } from "@/lib/api";
import { toast } from "sonner";

export function ReportDialog({ open, onClose, reportedUser, conversationId, messageId }) {
  const [reason, setReason] = useState("harassment");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reports", {
        reported_user_id: reportedUser.id,
        reason,
        details: details || null,
        conversation_id: conversationId || null,
        message_id: messageId || null,
      });
      toast.success(data.message);
      onClose(true);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#1F2A2E]/50" onClick={() => onClose(false)} />
      <div
        data-testid="report-dialog"
        className="relative w-full max-w-md bg-white rounded-2xl border border-[#E3DED4] shadow-2xl p-6"
      >
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-[#B42318]" />
          <h3 className="font-display text-xl font-bold text-[#1F2A2E]">
            Report {reportedUser?.display_name}
          </h3>
        </div>
        <p className="mt-2 text-sm text-[#4B5A60]">
          Reporting is always free and never requires a membership. Our moderation team reviews
          every report.
        </p>
        <div className="mt-4">
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
            Reason
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="report-reason-select"
            className="mt-1.5 w-full h-10 rounded-xl border border-[#E3DED4] bg-white px-3 text-sm"
          >
            {Object.entries(REPORT_REASON_LABELS).map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-3">
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
            What happened? (optional)
          </label>
          <Textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            data-testid="report-details-input"
            rows={4}
            placeholder="Share any details that help our team understand."
            className="mt-1.5 bg-white border-[#E3DED4]"
          />
        </div>
        <div className="mt-5 flex gap-2">
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            className="flex-1 bg-white border-[#E3DED4]"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="submit-report-button"
            className="flex-1 bg-[#B42318] hover:bg-[#94180F] text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Flag className="w-4 h-4 mr-1.5" /> Submit report</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, title, description, confirmLabel, onConfirm, onClose, danger }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#1F2A2E]/50" onClick={onClose} />
      <div
        data-testid="confirm-dialog"
        className="relative w-full max-w-sm bg-white rounded-2xl border border-[#E3DED4] shadow-2xl p-6"
      >
        <h3 className="font-display text-lg font-bold text-[#1F2A2E]">{title}</h3>
        <p className="mt-2 text-sm text-[#4B5A60] leading-6">{description}</p>
        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 bg-white border-[#E3DED4]">
            Cancel
          </Button>
          <Button
            data-testid="confirm-dialog-confirm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
              } finally {
                setBusy(false);
              }
            }}
            className={`flex-1 text-white ${danger ? "bg-[#B42318] hover:bg-[#94180F]" : "bg-[#1F6F78] hover:bg-[#185B62]"}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
